/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { Icon } from '@fluentui/react';
import { Separator } from '@fluentui/react/lib/Separator';
import { Stack } from '@fluentui/react/lib/Stack';
import { observer } from 'mobx-react';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocation } from 'react-router';
import { GlobalHeaderLinks } from 'src/appShell/globalHeader/utils/globalHeaderLinks';
import { ExternalLink } from 'src/core/components/ExternalLink';
import { useGlobalState } from 'src/core/hooks/useGlobalState';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { routeTable } from 'src/core/utils/routeTable';
import { doesRouteMatchLocation } from 'src/core/utils/routingUtils';
import { IntroSlides } from 'src/luis/verticals/introSlides/IntroSlides';

const messages = defineMessages({
    pricingLink: { id: 'portal.globalHelpBlade.pricingLink', defaultMessage: 'Pricing' },
    tutorialLink: { id: 'portal.globalHelpBlade.tutorialLink', defaultMessage: 'Tutorial' },
    azureBlogLink: { id: 'portal.globalHelpBlade.azureBlogLink', defaultMessage: 'Azure Blog' },
    trademarksLink: { id: 'portal.globalHelpBlade.trademarksLink', defaultMessage: 'Trademarks' },
    authoringApiLink: { id: 'portal.globalHelpBlade.authoringApiLink', defaultMessage: 'Authoring APIs' },
    documentationLink: { id: 'portal.globalHelpBlade.documentationLink', defaultMessage: 'Documentation' },
    stackOverflowLink: { id: 'portal.globalHelpBlade.stackOverflowLink', defaultMessage: 'Stack Overflow' },
    predictionApiLink: { id: 'portal.globalHelpBlade.predictionApiLink', defaultMessage: 'Prediction APIs' },
    a11yNotice: { id: 'portal.globalHelpBlade.a11yNotice', defaultMessage: 'Accessibility: Total Compliance' },
    azureSupportLink: { id: 'portal.globalHelpBlade.azureSupportLink', defaultMessage: 'Azure Support Plans' },
    privacyAndTermsLink: { id: 'portal.globalHelpBlade.privacyAndTermsLink', defaultMessage: 'Privacy & cookies' },
    termsOfUserLink: { id: 'portal.globalHelpBlade.termsOfUserLink', defaultMessage: 'Terms of User' },
    ticketLink: { id: 'portal.globalHelpBlade.ticketLink', defaultMessage: 'Create Azure support request to get help.' },
    previousExperience: { id: 'portal.globalHelpBlade.previousExperience', defaultMessage: 'Go back to previous experience' },
    developerCodeOfConductLink: { id: 'portal.globalHelpBlade.developerCodeOfConductLink', defaultMessage: 'Microsoft Code of Conduct' }
});

export const GlobalHelpBlade = observer(() => {
    const { pathname } = useLocation();
    const { uiStore } = useGlobalState();
    const { localize } = useLocalization();
    const [areIntroSlidesOpen, setAreIntroSlidesOpen] = React.useState(false);

    const isLuisPortal = uiStore.portal.kind === 'luis';
    const isCustomTextPortal = uiStore.portal.subPath === 'customText';
    const isFrenchLocale = uiStore.locale.code === 'fr';
    const isItalicLocale = uiStore.locale.code === 'it';

    let linksObj = {
        documentation: GlobalHeaderLinks.documentationHelpLink,
        authoringAPIs: GlobalHeaderLinks.authoringAPIsHelpLink,
        predictionAPIs: GlobalHeaderLinks.predictionAPIsHelpLink
    };

    if (isCustomTextPortal) {
        const isExtractionProject = doesRouteMatchLocation(routeTable.customText.extractionProjectsPage(), {
            locationToMatch: pathname
        });

        linksObj = {
            documentation: isExtractionProject
                ? GlobalHeaderLinks.extractionDocumentationHelpLink
                : GlobalHeaderLinks.classificationDocumentationHelpLink,
            authoringAPIs: GlobalHeaderLinks.customTextAuthoringAPIsLink,
            predictionAPIs: GlobalHeaderLinks.customTextPredictionAPIsLink
        };
    }

    return (
        <>
            <Stack verticalAlign="space-evenly" tokens={{ childrenGap: 30 }} grow>
                {(isLuisPortal || isCustomTextPortal) && [
                    <Stack verticalAlign="space-evenly" tokens={{ childrenGap: 10 }} grow>
                        <ExternalLink href={linksObj.documentation}>{localize(messages.documentationLink)}</ExternalLink>

                        {isLuisPortal && (
                            <ExternalLink onClick={() => setAreIntroSlidesOpen(true)}>{localize(messages.tutorialLink)}</ExternalLink>
                        )}
                        <ExternalLink href={linksObj.authoringAPIs}>{localize(messages.authoringApiLink)}</ExternalLink>

                        <ExternalLink href={linksObj.predictionAPIs}>{localize(messages.predictionApiLink)}</ExternalLink>

                        {isLuisPortal && (
                            <ExternalLink href={GlobalHeaderLinks.stackOverflowLink}>{localize(messages.stackOverflowLink)}</ExternalLink>
                        )}
                    </Stack>,
                    <Separator />
                ]}
                <Stack verticalAlign="space-evenly" tokens={{ childrenGap: 10 }} grow>
                    {isLuisPortal && <ExternalLink href={GlobalHeaderLinks.pricingLink}>{localize(messages.pricingLink)}</ExternalLink>}

                    <ExternalLink href={GlobalHeaderLinks.azureSupportLink}>{localize(messages.azureSupportLink)}</ExternalLink>

                    <ExternalLink href={GlobalHeaderLinks.requestTicketLink}>{localize(messages.ticketLink)}</ExternalLink>

                    <ExternalLink href={GlobalHeaderLinks.azureBlogLink}>{localize(messages.azureBlogLink)}</ExternalLink>

                    <ExternalLink href={GlobalHeaderLinks.privacyAndTermsLink}>{localize(messages.privacyAndTermsLink)}</ExternalLink>

                    <ExternalLink href={GlobalHeaderLinks.termsOfUserLink}>{localize(messages.termsOfUserLink)}</ExternalLink>

                    <ExternalLink href={GlobalHeaderLinks.trademarksLink}>{localize(messages.trademarksLink)}</ExternalLink>

                    <ExternalLink href={GlobalHeaderLinks.developerCodeOfConductLink}>
                        {localize(messages.developerCodeOfConductLink)}
                    </ExternalLink>

                    <Separator />

                    {isFrenchLocale && (
                        <Stack data-automation-id="a11yNotice" horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
                            <Icon iconName="AccessibiltyChecker" />
                            <ExternalLink href="https://www.microsoft.com/fr-fr/accessibility/accessibilite/declarations">
                                {localize(messages.a11yNotice)}
                            </ExternalLink>
                        </Stack>
                    )}

                    {isItalicLocale && (
                        <Stack data-automation-id="a11yNotice" horizontal tokens={{ childrenGap: 5 }} verticalAlign="center">
                            <Icon iconName="AccessibiltyChecker" />
                            <ExternalLink href="https://www.microsoft.com/it-it/accessibility/accessibilita/dichiarazioni">
                                Dichiarazione di accessibilità
                            </ExternalLink>
                        </Stack>
                    )}
                </Stack>
            </Stack>

            <IntroSlides isOpen={areIntroSlidesOpen} onDismiss={() => setAreIntroSlidesOpen(false)} />
        </>
    );
});
